/**
 * Home page content for the Cloakling marketing site (v0.1).
 *
 * Copy is derived from the Pencil prototype (frontend/pencil-dev/public-website.pen
 * frame "Home — Desktop", id Cyiqg). The prototype is the source of truth for
 * both layout and copy.
 */

export type HeroContent = {
  eyebrow: string;
  headline: string;
  subhead: string;
  primaryCta: string;
  secondaryCta: string;
  supporting: string;
};

export type MechanismStep = {
  number: string;
  title: string;
  body: string;
};

export type MechanismContent = {
  eyebrow: string;
  heading: string;
  body: string;
  steps: MechanismStep[];
};

export type ValuePropCard = {
  icon: "shield-check" | "link-2" | "eye" | "sparkles";
  title: string;
  body: string;
  visual?: "identity-map" | "screenshot-008";
};

export type ValuePropsContent = {
  eyebrow: string;
  heading: string;
  subhead: string;
  cards: ValuePropCard[];
};

export type AudiencePanel = {
  id: string;
  tag: string;
  heading: string;
  body: string;
  link: string;
  href: string;
  variant: "light" | "dark";
  screenshot: string;
};

export type AudienceContent = {
  eyebrow: string;
  heading: string;
  subhead: string;
  panels: AudiencePanel[];
};

export type HowItWorksStep = {
  number: string;
  title: string;
  body: string;
  visual: "upload" | "review-screenshot" | "tools";
};

export type HowItWorksContent = {
  eyebrow: string;
  heading: string;
  steps: HowItWorksStep[];
};

export type CleanRoomsContent = {
  tag: string;
  heading: string;
  body: string;
  bullets: string[];
  link: string;
};

export type TrustCard = {
  title: string;
  body: string;
  screenshot?: string;
};

export type TrustContent = {
  eyebrow: string;
  heading: string;
  subhead: string;
  cards: TrustCard[];
};

export type ClosingCTAContent = {
  eyebrow: string;
  heading: string;
  body: string;
  primary: string;
  secondary: string;
};

export type FooterContent = {
  tagline: string;
  columns: { heading: string; links: { label: string; href: string }[] }[];
  status: string;
};

export type HomeContent = {
  hero: HeroContent;
  mechanism: MechanismContent;
  valueProps: ValuePropsContent;
  audiences: AudienceContent;
  howItWorks: HowItWorksContent;
  cleanRooms: CleanRoomsContent;
  trust: TrustContent;
  closingCta: ClosingCTAContent;
  footer: FooterContent;
};

export const homeContent: HomeContent = {
  hero: {
    eyebrow: "Private document analysis for ChatGPT, Claude, and Gemini",
    headline: "Safely use ChatGPT on private documents.",
    subhead:
      "Cloakling automatically finds sensitive information in PDFs, replaces it with realistic substitutes or redacts it, and gives you an AI-ready copy you can safely upload to ChatGPT, Claude, Gemini, or work tools.",
    primaryCta: "Get access",
    secondaryCta: "See the 3-step flow",
    supporting:
      "Built for households and small offices that handle real people's information.",
  },
  mechanism: {
    eyebrow: "WHAT CLOAKLING MAKES",
    heading: "De-identify documents without breaking context.",
    body: "Redaction removes context. Cloakling detects names, emails, phone numbers, dates, account numbers, SSNs, and other private details, then de-identifies them while preserving enough context for AI to summarize, compare, and explain the document.",
    steps: [
      {
        number: "1",
        title: "Original PDF",
        body: "Real names, dates, addresses, account numbers, and records.",
      },
      {
        number: "2",
        title: "Reviewed cloak",
        body: "You confirm what changes, what stays, and what should be ignored.",
      },
      {
        number: "3",
        title: "AI-ready copy",
        body: "Consistent realistic stand-ins preserve the story without exposing the people.",
      },
    ],
  },
  valueProps: {
    eyebrow: "WHY PEOPLE USE IT",
    heading: "Keep sensitive data out of AI tools.",
    subhead:
      "Cloakling is built around a simple idea — the people inside a document don't need to follow it everywhere it goes.",
    cards: [
      {
        icon: "shield-check",
        title: "Mask information without breaking context.",
        body: "Cloakling creates a working copy AI can still summarize, compare, and explain. The private details change, but the document still reads like a document.",
      },
      {
        icon: "link-2",
        title: "Realistic substitutes keep the story straight.",
        body: '"John Smith" appears ten times, Cloakling uses the same substitute ten times. The story stays coherent for you and for the AI.',
        visual: "identity-map",
      },
      {
        icon: "eye",
        title: "Human review before export.",
        body: "Cloakling highlights detected names, numbers, dates, and identifiers so you can confirm, correct, or ignore them before you send a cloaked copy to an AI tool.",
      },
      {
        icon: "sparkles",
        title: "Works with the tools you already use.",
        body: "Use the cloaked copy in ChatGPT, Claude, Gemini, internal copilots, or other workflows. Cloakling does not require a new AI assistant.",
      },
    ],
  },
  audiences: {
    eyebrow: "TWO PLACES THIS MATTERS MOST",
    heading: "For the paperwork at home — and the office that handles it.",
    subhead:
      "Whether you're handling your kid's school forms or a client's tax notice, Cloakling treats the people inside your documents the same way.",
    panels: [
      {
        id: "personal",
        tag: "AT HOME",
        heading: "Get AI help with paperwork without uploading your household.",
        body: "Medical bills, insurance letters, mortgage packets, school forms, custody paperwork, travel itineraries — the documents you actually want help with are the ones you'd never want a stranger to read line by line. Cloakling lets you ask the questions without uploading a roster of your household.",
        link: "Get access",
        href: "https://cloakling.dev",
        variant: "light",
        screenshot: "/product-screenshots/008-cloakling.png",
      },
      {
        id: "small-offices",
        tag: "IN SMALL OFFICES",
        heading:
          "Let staff use AI without pasting client records into public tools.",
        body: 'Family medical practices, small law firms, accounting offices, school districts, real estate teams. Anywhere "I need help with this client\'s paperwork" is a real workflow — and "I pasted a client\'s return into ChatGPT" is a real career-ender. Cloakling lets you bring AI into the office without bringing client records along.',
        link: "Talk to us about small-office use",
        href: "mailto:hello@cloakling.com",
        variant: "dark",
        screenshot: "/product-screenshots/0011-cloakling.png",
      },
    ],
  },
  howItWorks: {
    eyebrow: "HOW IT WORKS",
    heading: "From sensitive PDF to AI-ready copy in three steps.",
    steps: [
      {
        number: "1",
        title: "Safely upload the sensitive PDF.",
        body: "Start with the file you already have. No retyping and no manual black-bar redaction.",
        visual: "upload",
      },
      {
        number: "2",
        title: "Review the private details.",
        body: "Cloakling highlights names, addresses, dates, account numbers, and other identifiers so you can confirm, correct, or ignore each one.",
        visual: "review-screenshot",
      },
      {
        number: "3",
        title: "Export the AI-ready copy.",
        body: "Use the cloaked version in ChatGPT, Claude, Gemini, or an internal tool. The AI gets the document context, not the real people.",
        visual: "tools",
      },
    ],
  },
  cleanRooms: {
    tag: "CLOAKED CLEAN ROOMS",
    heading: "Shared cloaked workspaces for small teams.",
    body: "Cloaked Clean Rooms let families or small business practices, firms, schools, and vendors work from the same cloaked document set, with permissions for who can see originals and who only sees the cloaked copy.",
    bullets: [
      "A doctor's office, a law firm, a school district: same documents, shared safely.",
      "Permissions for who sees the cloaked version and who sees the originals.",
      "Same Cloakling underneath. Shared, with the rules clearly drawn.",
    ],
    link: "Learn More",
  },
  trust: {
    eyebrow: "TRUST BY DESIGN",
    heading: `Don't hand every detail of your life to AI.`,
    subhead: "Secure your documents explicitly before using AI tools.",
    cards: [
      {
        title: "Choose what to cloak, censor, remove, or leave alone.",
        body: "Different documents need different strictness. Cloakling keeps those choices visible instead of hiding them inside a black box.",
        screenshot: "/product-screenshots/004-cloakling.png",
      },
      {
        title: "Clear data handling from day one.",
        body: "Storage, retention, deletion, and training-policy details are spelled out before sensitive workflows go live — not buried in fine print.",
        screenshot: "/product-screenshots/005-cloakling.png",
      },
    ],
  },
  closingCta: {
    eyebrow: "THE MIDDLE PATH",
    heading:
      "You wouldn't hand your unlocked iPhone to a stranger. Don't hand sensitive files to one either.",
    body: "Cloakling gives households and small offices a middle path: keep the useful document context, cloak the real personal details, and use the AI tools you already rely on.",
    primary: "Get access",
    secondary: "Talk to us",
  },
  footer: {
    tagline:
      "Use AI on sensitive documents without uploading the real people inside them. Built for households and small offices.",
    columns: [
      {
        heading: "PRODUCT",
        links: [
          { label: "For Personal", href: "#personal" },
          { label: "Small Offices", href: "#small-offices" },
          { label: "How it works", href: "#how-it-works" },
        ],
      },
      {
        heading: "COMPANY",
        links: [
          { label: "About", href: "#" },
          { label: "Talk to us", href: "mailto:hello@cloakling.com" },
        ],
      },
      {
        heading: "LEGAL",
        links: [
          { label: "Privacy", href: "#" },
          { label: "Terms", href: "#" },
          { label: "Security", href: "#" },
        ],
      },
    ],
    status: "Privacy-preserving by default",
  },
};
